import { useState } from 'react';
import { Button, Checkbox } from '@moneyskyline/ui';
import { useBatchUndo } from '@/api/hooks/useBatchUndo';
import { useJob } from '@/api/hooks/useJob';
import { useApplyRuleSnapshot, useRuleMatchSnapshot } from '@/api/hooks/useRuleApply';
import type { RuleRow } from '@/api/hooks/useRules';

export function RuleApplyResult({ rule }: { rule: Pick<RuleRow, 'rule_id' | 'version'> }) {
  const snapshot = useRuleMatchSnapshot(rule.rule_id);
  const apply = useApplyRuleSnapshot();
  const undo = useBatchUndo();
  const [acknowledge, setAcknowledge] = useState(false);
  const jobId = apply.data?.job_id ?? null;
  const job = useJob(jobId);
  const partial = snapshot.data?.coverage.partial_source ?? false;
  const knownCount = snapshot.data?.known_match_count;
  const applied = typeof job.data?.progress.applied_count === 'number' ? job.data.progress.applied_count : null;
  const unchanged = typeof job.data?.progress.unchanged_count === 'number' ? job.data.progress.unchanged_count : null;

  function applySnapshot() {
    if (!snapshot.data) return;
    const operationId = globalThis.crypto?.randomUUID?.();
    if (!operationId) return;
    apply.mutate({ rule_id: rule.rule_id, operation_id: operationId, match_snapshot_id: snapshot.data.match_snapshot_id, expected_rule_version: snapshot.data.rule_version, acknowledge_partial_source: acknowledge });
  }

  if (snapshot.isPending) return <p role="status" data-testid="rule-result-loading">Counting current matches…</p>;
  if (snapshot.isError || !snapshot.data) return <p role="alert" className="text-degraded-foreground" data-testid="rule-result-error">Could not load the current match count.</p>;
  if (snapshot.data.state === 'failed' || snapshot.data.state === 'rebuilding') return <p role="status" data-testid="rule-result-unavailable">The match count is {snapshot.data.state}. Nothing can be applied yet.</p>;

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid="rule-apply-result">
      <p>Current match count: <strong>{knownCount}</strong></p>
      {partial && <label className="mt-2 flex items-start gap-2 text-sm"><Checkbox checked={acknowledge} onCheckedChange={(checked) => setAcknowledge(checked === true)} /><span>Some sources are incomplete; apply only to the reviewed coverage.</span></label>}
      {!jobId && <Button type="button" className="mt-3" disabled={apply.isPending || (partial && !acknowledge)} onClick={applySnapshot}>Apply this condition</Button>}
      {apply.isError && <p role="alert" className="mt-2 text-sm text-degraded-foreground">{apply.error.name === 'RuleApplyConflictError' ? 'This condition changed. Refresh the count before applying.' : 'Could not start applying this condition.'}</p>}
      {jobId && <JobResult job={job.data} applied={applied} unchanged={unchanged} batchId={apply.data?.batch_id ?? null} onUndo={() => apply.data?.batch_id && undo.undo.mutate(apply.data.batch_id)} />}
    </div>
  );
}

function JobResult({ job, applied, unchanged, batchId, onUndo }: { job: ReturnType<typeof useJob>['data']; applied: number | null; unchanged: number | null; batchId: string | null; onUndo: () => void }) {
  if (!job) return <p role="status" aria-busy="true">Applying condition…</p>;
  if (job.status === 'queued' || job.status === 'running') return <p role="status" aria-busy="true">Applying condition…</p>;
  if (job.status === 'failed') return <p role="alert" className="text-degraded-foreground">Applying this condition stopped before it finished. The final count is unavailable.</p>;
  if (job.status === 'cancelled') return <p role="status">Applying this condition was cancelled.</p>;
  return <div role="status"><p>Condition apply complete{applied !== null ? ` · ${applied} applied` : ''}{unchanged !== null ? ` · ${unchanged} unchanged` : ''}.</p>{applied !== null && applied > 0 && batchId && <Button type="button" variant="outline" className="mt-2" onClick={onUndo}>Undo item changes</Button>}</div>;
}

export default RuleApplyResult;
